<template>
    <div class="product-detail-view">
        <!-- En-tête avec navigation de retour -->
        <div class="page-header">
            <div class="back-navigation">
                <button class="back-button" @click="goBack">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Retour aux produits
                </button>
            </div>
            <h1 class="page-heading">Détails du produit</h1>
        </div>

        <!-- Loader pendant le chargement -->
        <div v-if="loading" class="loader-container">
            <div class="spinner"></div>
            <p>Chargement des détails du produit...</p>
        </div>

        <!-- Message d'erreur -->
        <div v-else-if="error" class="error-container">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <h3>Une erreur est survenue</h3>
            <p>{{ error }}</p>
            <button class="retry-button" @click="loadProductDetails">
                Réessayer
            </button>
        </div>

        <!-- Contenu principal des détails du produit -->
        <div v-else-if="product" class="product-detail-content">
            <div class="product-detail-container">
                <!-- Section d'images -->
                <div class="product-image-section">
                    <div class="product-image-container">
                        <img v-if="product.image_url" :src="product.image_url" :alt="product.name"
                            class="product-main-image">
                        <div v-else class="product-no-image">
                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                stroke-linejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                            <p>Aucune image disponible</p>
                        </div>
                    </div>
                </div>

                <!-- Section d'informations -->
                <div class="product-info-section">
                    <div class="product-header">
                        <h2 class="product-name">{{ product.name }}</h2>
                        <div class="product-badges">
                            <span v-if="product.category" class="product-category-badge">
                                {{ getCategoryName(product.category_id) }}
                            </span>
                            <span v-if="product.stock <= 5" class="product-stock-badge low-stock">
                                Stock faible: {{ product.stock }} unités
                            </span>
                            <span v-else class="product-stock-badge">
                                En stock: {{ product.stock }} unités
                            </span>
                        </div>
                    </div>

                    <div class="product-price-section">
                        <div class="product-price-container">
                            <div class="product-price">{{ formatPrice(product.price_incl_tax) }}</div>
                            <div class="product-price-details">
                                <span class="price-tax-label">Prix HT: {{ formatPrice(product.price_excl_tax) }}</span>
                                <span class="price-tax-rate">TVA: {{ product.tax_rate }}%</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="product.description" class="product-description-section">
                        <h3 class="section-title">Description</h3>
                        <p class="product-description">{{ product.description }}</p>
                    </div>

                    <div class="product-details-section">
                        <h3 class="section-title">Détails du produit</h3>
                        <div class="product-details-grid">
                            <div class="detail-row">
                                <div class="detail-label">SKU</div>
                                <div class="detail-value">{{ product.sku || '-' }}</div>
                            </div>
                            <div class="detail-row">
                                <div class="detail-label">Code-barres</div>
                                <div class="detail-value">{{ product.barcode || '-' }}</div>
                            </div>
                            <div class="detail-row">
                                <div class="detail-label">Référence</div>
                                <div class="detail-value">{{ product.reference || '-' }}</div>
                            </div>
                            <div class="detail-row">
                                <div class="detail-label">Unité</div>
                                <div class="detail-value">{{ product.unit || '-' }}</div>
                            </div>
                            <div class="detail-row">
                                <div class="detail-label">Catégorie</div>
                                <div class="detail-value">{{ getCategoryName(product.category_id) || '-' }}</div>
                            </div>
                            <div class="detail-row">
                                <div class="detail-label">Date de création</div>
                                <div class="detail-value">{{ formatDate(product.created_at) }}</div>
                            </div>
                            <div class="detail-row">
                                <div class="detail-label">Dernière mise à jour</div>
                                <div class="detail-value">{{ formatDate(product.updated_at) }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Section de gestion du stock -->
                    <div v-if="product.track_inventory" class="product-stock-section">
                        <h3 class="section-title">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <path
                                    d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z">
                                </path>
                            </svg>
                            Gestion du stock
                        </h3>
                        <div class="stock-stats-grid">
                            <div class="stock-stat-card">
                                <div class="stock-stat-label">Quantité actuelle</div>
                                <div class="stock-stat-value primary">{{ product.stock_quantity || 0 }} {{ product.unit
                                    || '' }}</div>
                                <div :class="['stock-status-badge', getStockStatusInfo().class]">
                                    {{ getStockStatusInfo().label }}
                                </div>
                            </div>
                            <div class="stock-stat-card">
                                <div class="stock-stat-label">Stock minimum</div>
                                <div class="stock-stat-value">{{ product.minimum_stock || 0 }} {{ product.unit || '' }}
                                </div>
                            </div>
                            <div class="stock-stat-card">
                                <div class="stock-stat-label">Stock maximum</div>
                                <div class="stock-stat-value">{{ product.maximum_stock || '-' }} {{ product.unit || ''
                                    }}</div>
                            </div>
                            <div class="stock-stat-card">
                                <div class="stock-stat-label">Valeur du stock</div>
                                <div class="stock-stat-value">{{ formatPrice((product.stock_quantity || 0) *
                                    (product.price_excl_tax || 0)) }}</div>
                            </div>
                        </div>
                    </div>



                    <!-- Actions disponibles -->
                    <div class="product-actions">
                        <button class="edit-product-button" @click="editProduct">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                            </svg>
                            Modifier
                        </button>
                        <button class="delete-product-button" @click="confirmDelete">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path
                                    d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2">
                                </path>
                            </svg>
                            Supprimer
                        </button>
                    </div>
                </div>
            </div>
            <br>

            <!-- Ventes du produit -->
            <div class="history-container">
                <ProductSalesHistory :product-id="product?.id" :limit="10" />
            </div>
            <br>
            <!-- Historique des mouvements de stock -->
            <div v-if="product && product.track_inventory" class="history-container">
                <ProductStockTransactions :product-id="product?.id"  />
            </div>


        </div>

        <!-- Modal de confirmation de suppression -->
        <div v-if="showDeleteModal" class="modal-overlay">
            <div class="modal-container delete-modal">
                <div class="modal-header">
                    <h3>Confirmer la suppression</h3>
                    <button class="close-btn" @click="showDeleteModal = false">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
                <div class="modal-body text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="text-danger mb-4">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                    <p>Êtes-vous sûr de vouloir supprimer le produit <strong>{{ product?.name }}</strong> ?</p>
                    <p class="text-danger">Cette action est irréversible.</p>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-secondary" @click="showDeleteModal = false">Annuler</button>
                    <button class="btn btn-danger" @click="deleteProduct" :disabled="deleteLoading">
                        <span v-if="deleteLoading" class="spinner-border spinner-border-sm mr-1"></span>
                        Supprimer
                    </button>
                </div>
            </div>
        </div>

        <!-- Modal de modification du produit -->
        <EditProductModal v-if="showEditModal" :show="showEditModal" :product="product" :categories="categories"
            @close="showEditModal = false" @submit="handleProductUpdate" />
    </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { productsService, categoriesService, inventoryTransactionService } from '@/services';
import { useToastStore } from '@/store/modules';
import EditProductModal from '@/views/products/EditProductModal.vue';
import ProductSalesHistory from '@/components/products/ProductSalesHistory.vue';
import ProductStockTransactions from '@/components/products/ProductStockTransactions.vue';

export default {
    name: 'ProductDetailView',
    components: {
        EditProductModal,
        ProductSalesHistory,
        ProductStockTransactions
    },
    setup() {
        const route = useRoute();
        const router = useRouter();
        const toastStore = useToastStore();

        // État des données
        const product = ref(null);
        const productSales = ref([]);
        const productTransactions = ref([]);
        const categories = ref([]);

        // État UI
        const loading = ref(true);
        const error = ref(null);
        const showDeleteModal = ref(false);
        const showEditModal = ref(false);
        const deleteLoading = ref(false);
        const transactionsLoading = ref(false);
        const transactionsPagination = ref({
            currentPage: 1,
            totalPages: 1,
            total: 0,
            perPage: 10
        });

        // Obtenir l'ID du produit à partir de l'URL
        const productId = computed(() => route.params.id);

        // Charger les détails du produit
        const loadProductDetails = async () => {
            loading.value = true;
            error.value = null;

            try {
                // Récupérer les détails du produit
                const response = await productsService.getProduct(productId.value, {
                    "with[]": ['category', 'media'] // Charger les relations si l'API les supporte
                });

                // Extraire les données
                product.value = response.data.product || response.data;

                // Charger les catégories si pas encore chargées
                if (categories.value.length === 0) {
                    await fetchCategories();
                }

            } catch (err) {
                console.error("Erreur lors du chargement du produit:", err);
                error.value = "Impossible de charger les détails du produit. Veuillez réessayer.";

                // Afficher une notification d'erreur
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement des détails du produit.',
                    duration: 5000
                });
            } finally {
                loading.value = false;
            }
        };

        // Charger la liste des ventes du produit si non incluse dans la réponse précédente
        const loadProductSales = async () => {
            // if (!product.value || productSales.value.length > 0) return;

            try {
                // Cette méthode dépend de votre API, ajustez si nécessaire
                const response = await productsService.getProductSales(productId.value);
                console.log(response);
                // Adapter la structure de la réponse selon le format attendu
                if (response && response.data) {
                    // Assigner directement si le format correspond déjà
                    productSales.value = response.data.data;
                }
            } catch (err) {
                console.error("Erreur lors du chargement des ventes:", err);
                // Ne pas afficher d'erreur, c'est une fonctionnalité secondaire
            }
        };

        // Charger les catégories pour l'affichage et le modal de modification
        const fetchCategories = async () => {
            try {
                const response = await categoriesService.getCategories({
                    perPage: 100, // Récupérer un grand nombre de catégories
                    page: 1
                });

                if (response && response.data) {
                    if (response.data.data) {
                        // Format paginé
                        categories.value = response.data.data;
                    } else {
                        // Format simple
                        categories.value = response.data;
                    }
                }
            } catch (err) {
                console.error("Erreur lors du chargement des catégories:", err);

                // Ne pas afficher d'erreur, c'est une fonctionnalité secondaire
            }
        };

        // Charger les transactions d'inventaire du produit
        const loadProductTransactions = async (page = 1) => {
            // if (!product.value) return;

            transactionsLoading.value = true;
            try {
                const response = await inventoryTransactionService.getTransactions({
                    product_id: productId.value,
                    per_page: transactionsPagination.value.perPage,
                    page: page
                });

                if (response && response.data) {
                    // Vérifier si c'est une réponse paginée
                    if (response.data.data) {
                        productTransactions.value = response.data.data;

                        // Mettre à jour les informations de pagination
                        transactionsPagination.value = {
                            currentPage: response.data.current_page || page,
                            totalPages: response.data.last_page || 1,
                            total: response.data.total || 0,
                            perPage: response.data.per_page || 20
                        };
                    } else {
                        // Réponse non paginée
                        productTransactions.value = response.data;
                    }
                }
            } catch (err) {
                console.error("Erreur lors du chargement des transactions:", err);
                // Ne pas afficher d'erreur, c'est une fonctionnalité secondaire
            } finally {
                transactionsLoading.value = false;
            }
        };

        // Charger la page suivante des transactions
        const loadNextTransactionsPage = () => {
            if (transactionsPagination.value.currentPage < transactionsPagination.value.totalPages) {
                loadProductTransactions(transactionsPagination.value.currentPage + 1);
            }
        };

        // Charger la page précédente des transactions
        const loadPreviousTransactionsPage = () => {
            if (transactionsPagination.value.currentPage > 1) {
                loadProductTransactions(transactionsPagination.value.currentPage - 1);
            }
        };

        // Navigation vers la liste des produits
        const goBack = () => {
            router.push({ name: 'products' });
        };

        // Ouvrir le modal d'édition
        const editProduct = () => {
            showEditModal.value = true;
        };

        // Gérer la mise à jour du produit depuis le modal
        const handleProductUpdate = async (formData) => {
            try {
                const productId = product.value.id;
                const data = new FormData();

                // Ajouter tous les champs du formulaire
                Object.keys(formData).forEach(key => {
                    if (key !== 'image' && key !== 'id' && key !== 'image_url' && key !== 'remove_existing_image' && formData[key] !== null && formData[key] !== undefined) {
                        data.append(key, formData[key]);
                    }
                });

                // Gérer l'image
                if (formData.image && formData.image instanceof File) {
                    // Nouvelle image sélectionnée
                    data.append('image', formData.image);
                } else if (formData.remove_existing_image) {
                    // Supprimer l'image existante
                    data.append('remove_image', '1');
                }
                // Si aucune nouvelle image et pas de suppression, garder l'image existante

                // Appel au service
                await productsService.updateProduct(productId, data);

                // Fermer le modal
                showEditModal.value = false;

                // Afficher une notification de succès
                toastStore.addToast({
                    type: 'success',
                    message: 'Produit mis à jour avec succès !'
                });

                // Recharger les détails du produit
                await loadProductDetails();
            } catch (err) {
                console.error("Erreur lors de la mise à jour du produit:", err);

                // Afficher une notification d'erreur
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de la mise à jour du produit'
                });
            }
        };

        // Confirmation de suppression
        const confirmDelete = () => {
            showDeleteModal.value = true;
        };

        // Supprimer le produit
        const deleteProduct = async () => {
            deleteLoading.value = true;

            try {
                // Appel au service
                await productsService.deleteProduct(product.value.id);

                // Fermer le modal
                showDeleteModal.value = false;

                // Afficher une notification de succès
                toastStore.addToast({
                    type: 'success',
                    message: 'Produit supprimé avec succès !'
                });

                // Rediriger vers la liste des produits
                router.push({ name: 'products' });
            } catch (err) {
                console.error("Erreur lors de la suppression du produit:", err);

                // Afficher une notification d'erreur
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de la suppression du produit'
                });
            } finally {
                deleteLoading.value = false;
            }
        };

        // Formater un prix
        const formatPrice = (price) => {
            if (!price && price !== 0) return '-';

            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0
            }).format(price);
        };

        // Formater une date
        const formatDate = (dateString) => {
            if (!dateString) return '-';

            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        };

        // Obtenir le nom d'une catégorie par son ID
        const getCategoryName = (categoryId) => {
            if (!categoryId) return '';

            const category = categories.value.find(cat => cat.id === categoryId);
            return category ? category.name : '';
        };

        // Obtenir le type de transaction traduit
        const getTransactionTypeLabel = (type) => {
            const labels = {
                'purchase': 'Achat',
                'sale': 'Vente',
                'adjustment': 'Ajustement',
                'return': 'Retour',
                'damage': 'Perte/Dommage',
                'transfer': 'Transfert'
            };
            return labels[type] || type;
        };

        // Obtenir la classe CSS pour le type de transaction
        const getTransactionTypeClass = (type) => {
            const classes = {
                'purchase': 'transaction-purchase',
                'sale': 'transaction-sale',
                'adjustment': 'transaction-adjustment',
                'return': 'transaction-return',
                'damage': 'transaction-damage',
                'transfer': 'transaction-transfer'
            };
            return classes[type] || '';
        };

        // Obtenir le statut du stock
        const getStockStatusInfo = () => {
            if (!product.value || !product.value.track_inventory) {
                return { label: 'Non suivi', class: 'stock-not-tracked', icon: 'minus' };
            }

            const stock = product.value.stock_quantity || 0;
            const min = product.value.minimum_stock || 0;

            if (stock <= 0) {
                return { label: 'Rupture de stock', class: 'stock-out', icon: 'x' };
            } else if (stock <= min) {
                return { label: 'Stock faible', class: 'stock-low', icon: 'alert-triangle' };
            } else {
                return { label: 'En stock', class: 'stock-good', icon: 'check' };
            }
        };

        // Charger les données au montage du composant
        onMounted(() => {
            loadProductDetails();
            loadProductSales();
            loadProductTransactions();
        });

        return {
            // État
            product,
            productSales,
            productTransactions,
            categories,
            loading,
            error,
            showDeleteModal,
            showEditModal,
            deleteLoading,
            transactionsLoading,
            transactionsPagination,

            // Méthodes
            loadProductDetails,
            loadProductTransactions,
            loadNextTransactionsPage,
            loadPreviousTransactionsPage,
            goBack,
            editProduct,
            handleProductUpdate,
            confirmDelete,
            deleteProduct,
            formatPrice,
            formatDate,
            getCategoryName,
            getTransactionTypeLabel,
            getTransactionTypeClass,
            getStockStatusInfo
        };
    }
};
</script>

<style scoped>
.product-detail-view {
    padding: 0 0 30px 0;
}

/* En-tête avec navigation */
.page-header {
    display: flex;
    flex-direction: column;
    margin-bottom: 24px;
}

.back-navigation {
    margin-bottom: 12px;
}

.back-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    padding: 8px 0;
    color: #0A5C6B;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.back-button:hover {
    color: #094854;
}

.page-heading {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
}

/* Loader */
.loader-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    text-align: center;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #0A5C6B;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* Conteneur d'erreur */
.error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    text-align: center;
}

.error-container svg {
    color: #ef4444;
    margin-bottom: 16px;
}

.error-container h3 {
    font-size: 20px;
    color: #1e293b;
    margin-bottom: 8px;
}

.error-container p {
    color: #64748b;
    max-width: 400px;
    margin-bottom: 16px;
}

.retry-button {
    padding: 10px 20px;
    background-color: #0A5C6B;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.retry-button:hover {
    background-color: #094854;
}

/* Layout principal des détails */
.product-detail-content {
    margin-bottom: 30px;
}

.product-detail-container {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 30px;
    background-color: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.history-container {
    margin-bottom: 20px;
    padding: 24px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    background-color: white;
    border-radius: 12px;
}

/* Section d'image */
.product-image-section {
    padding: 20px;
    background-color: #f8fafc;
    border-right: 1px solid #e2e8f0;
}

.product-image-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 8px;
}

.product-main-image {
    width: 100%;
    height: auto;
    max-height: 400px;
    object-fit: contain;
}

.product-no-image {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 300px;
    background-color: #f1f5f9;
    color: #94a3b8;
    border-radius: 8px;
}

.product-no-image p {
    margin-top: 16px;
    font-size: 14px;
}

/* Section d'informations */
.product-info-section {
    padding: 30px;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.product-header {
    margin-bottom: 20px;
}

.product-name {
    font-size: 26px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 12px 0;
}

.product-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
}

.product-category-badge {
    display: inline-block;
    padding: 6px 12px;
    background-color: #ecfdf5;
    color: #059669;
    font-size: 13px;
    font-weight: 500;
    border-radius: 20px;
}

.product-stock-badge {
    display: inline-block;
    padding: 6px 12px;
    background-color: #f0f9ff;
    color: #0284c7;
    font-size: 13px;
    font-weight: 500;
    border-radius: 20px;
}

.product-stock-badge.low-stock {
    background-color: #fff7ed;
    color: #c2410c;
}

/* Section prix */
.product-price-section {
    margin-bottom: 6px;
}

.product-price-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.product-price {
    font-size: 32px;
    font-weight: 700;
    color: #0A5C6B;
}

.product-price-details {
    display: flex;
    gap: 12px;
    font-size: 14px;
    color: #64748b;
}

/* Section description */
.product-description-section {
    padding: 6px 0;
    border-top: 1px solid #e2e8f0;
}

.section-title {
    font-size: 18px;
    font-weight: 600;
    color: #334155;
    margin: 0 0 12px 0;
}

.product-description {
    font-size: 15px;
    line-height: 1.6;
    color: #475569;
    white-space: pre-line;
}

/* Grille de détails */
.product-details-section {
    padding: 6px 0;
    border-top: 1px solid #e2e8f0;
}

.product-details-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px 30px;
}

.detail-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.detail-label {
    font-size: 13px;
    color: #64748b;
}

.detail-value {
    font-size: 15px;
    color: #1e293b;
    font-weight: 500;
}

/* Section de gestion du stock */
.product-stock-section {
    padding: 24px 0;
    border-top: 1px solid #e2e8f0;
}

.section-title {
    display: flex;
    align-items: center;
    gap: 8px;
}

.stock-stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-top: 16px;
}

.stock-stat-card {
    padding: 20px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    position: relative;
}

.stock-stat-label {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 8px;
    font-weight: 500;
}

.stock-stat-value {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
}

.stock-stat-value.primary {
    color: #0A5C6B;
}

.stock-status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
}

.stock-status-badge.stock-good {
    background: #dcfce7;
    color: #166534;
}

.stock-status-badge.stock-low {
    background: #fef3c7;
    color: #92400e;
}

.stock-status-badge.stock-out {
    background: #fee2e2;
    color: #991b1b;
}

.stock-status-badge.stock-not-tracked {
    background: #f3f4f6;
    color: #6b7280;
}

/* Section transactions */
.product-transactions-section {
    padding: 0;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.btn-refresh {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    color: #64748b;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #0A5C6B;
    color: #0A5C6B;
}

.btn-refresh:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-refresh svg {
    transition: transform 0.3s;
}

.btn-refresh:hover:not(:disabled) svg {
    transform: rotate(180deg);
}

.transactions-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px;
}

.transactions-pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    margin-top: 16px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.pagination-info {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
}

.pagination-controls {
    display: flex;
    gap: 8px;
}

.pagination-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    color: #64748b;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
    background: #0A5C6B;
    border-color: #0A5C6B;
    color: white;
}

.pagination-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.no-transactions {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 20px;
    color: #9ca3af;
}

.no-transactions svg {
    margin-bottom: 16px;
}

.product-transactions-table-container {
    overflow-x: auto;
    margin-top: 16px;
}

.product-transactions-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}

.product-transactions-table th,
.product-transactions-table td {
    padding: 14px 16px;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
}

.product-transactions-table th {
    background-color: #f8fafc;
    font-weight: 600;
    color: #334155;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.product-transactions-table td {
    color: #475569;
}

.product-transactions-table tr:hover td {
    background-color: #f8fafc;
}

.transaction-type-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}

.transaction-purchase {
    background: #dcfce7;
    color: #166534;
}

.transaction-sale {
    background: #dbeafe;
    color: #1e40af;
}

.transaction-adjustment {
    background: #fef3c7;
    color: #92400e;
}

.transaction-return {
    background: #e0e7ff;
    color: #4338ca;
}

.transaction-damage {
    background: #fee2e2;
    color: #991b1b;
}

.transaction-transfer {
    background: #f3e8ff;
    color: #7e22ce;
}

.quantity-positive {
    color: #166534;
    font-weight: 600;
}

.quantity-negative {
    color: #991b1b;
    font-weight: 600;
}

.transaction-notes {
    max-width: 250px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Section ventes */
.product-sales-section {
    padding: 0;
}

.product-sales-table-container {
    overflow-x: auto;
    margin-top: 16px;
}

.product-sales-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}

.product-sales-table th,
.product-sales-table td {
    padding: 14px 16px;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
}

.product-sales-table th {
    background-color: #f8fafc;
    font-weight: 600;
    color: #334155;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.product-sales-table td {
    color: #475569;
}

.product-sales-table tr:hover td {
    background-color: #f8fafc;
}

@media (max-width: 768px) {
    .stock-stats-grid {
        grid-template-columns: 1fr;
    }

    .product-transactions-table,
    .product-sales-table {
        min-width: 650px;
    }

    .product-transactions-table-container,
    .product-sales-table-container {
        margin: 0 -20px;
        padding: 0 20px;
        overflow-x: auto;
    }

    .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .btn-refresh {
        width: 100%;
        justify-content: center;
    }

    .transactions-pagination {
        flex-direction: column;
        gap: 12px;
    }

    .pagination-controls {
        width: 100%;
        justify-content: space-between;
    }

    .pagination-btn {
        flex: 1;
    }
}

/* Actions */
.product-actions {
    display: flex;
    gap: 12px;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #e2e8f0;
}

.edit-product-button,
.delete-product-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.edit-product-button {
    background-color: #0A5C6B;
    color: white;
    border: none;
}

.edit-product-button:hover {
    background-color: #094854;
}

.delete-product-button {
    background-color: white;
    color: #ef4444;
    border: 1px solid #fecaca;
}

.delete-product-button:hover {
    background-color: #fef2f2;
}

/* Responsive design */
@media (max-width: 992px) {
    .product-detail-container {
        grid-template-columns: 1fr;
    }

    .product-image-section {
        border-right: none;
        border-bottom: 1px solid #e2e8f0;
    }

    .product-main-image {
        max-height: 300px;
    }
}

@media (max-width: 768px) {
    .product-details-grid {
        grid-template-columns: 1fr;
    }

    .product-actions {
        flex-direction: column;
    }

    .edit-product-button,
    .delete-product-button {
        width: 100%;
    }
}

@media (max-width: 576px) {
    .product-info-section {
        padding: 20px;
    }

    .product-price {
        font-size: 28px;
    }

    .product-price-details {
        flex-direction: column;
        gap: 4px;
    }
}

/* Modal de suppression */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 20px;
}

.modal-container {
    background-color: white;
    border-radius: 12px;
    width: 100%;
    max-width: 500px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #0A5C6B;
}

.close-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #64748b;
}

.close-btn:hover {
    color: #0A5C6B;
}
</style>